import React from "react";
import { Box, Card, Chip, Container, Grid, Stack, Typography } from "@mui/material";
import { motion } from "framer-motion";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";

const REDGRAD = "linear-gradient(135deg, #7A0E0E 0%, #B71C1C 50%, #E64A19 100%)";
const AMBERGRAD = "linear-gradient(135deg, #FF8F00 0%, #FFB300 50%, #FFC107 100%)";

const VALUES = [
  { icon: <LocalFireDepartmentIcon />, title: "Passion", desc: "Every pizza is a love letter to flavor." },
  { icon: <FavoriteRoundedIcon />, title: "Quality", desc: "Only the finest, freshest ingredients." },
  { icon: <EmojiEventsRoundedIcon />, title: "Excellence", desc: "Award-winning recipes, perfected daily." },
  { icon: <GroupsRoundedIcon />, title: "Community", desc: "Feeding families, building memories." },
];

const TIMELINE = [
  { year: "2010", title: "The Spark", desc: "Started in a tiny garage with one wood-fired oven." },
  { year: "2014", title: "First Store", desc: "Opened our flagship location downtown." },
  { year: "2019", title: "Going National", desc: "Expanded to 12 cities across the country." },
  { year: "2026", title: "50K Foodies", desc: "Now serving 50,000+ happy customers monthly." },
];

const TEAM = [
  { name: "Marco Rossi", role: "Head Chef", emoji: "👨‍🍳" },
  { name: "Sofia Bianchi", role: "Co-Founder", emoji: "👩‍💼" },
  { name: "Luca Romano", role: "Pizza Master", emoji: "🧑‍🍳" },
  { name: "Emma Verde", role: "Operations", emoji: "👩‍🔧" },
];

const AboutPage = () => {
  return (
    <Box sx={{ background: "#FFF8F0", overflow: "hidden" }}>
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          py: { xs: 10, md: 14 },
          background:
            "radial-gradient(800px 400px at 20% 30%, rgba(255,179,0,0.2), transparent 60%), radial-gradient(700px 400px at 80% 70%, rgba(230,74,25,0.2), transparent 60%), #FFF8F0",
          textAlign: "center",
          px: 3,
        }}
      >
        <motion.div
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 6, repeat: Infinity }}
          style={{
            position: "absolute", top: "20%", left: "10%",
            width: 180, height: 180, borderRadius: "50%",
            background: REDGRAD, filter: "blur(70px)", opacity: 0.3,
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative" }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Chip label="OUR STORY" sx={{ mb: 3, fontWeight: 800, letterSpacing: 2, background: "rgba(183,28,28,0.1)", color: "#B71C1C" }} />
            <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 68 }, lineHeight: 1.05, mb: 3 }}>
              From a tiny oven to{" "}
              <Box component="span" sx={{ background: REDGRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                a foodie revolution.
              </Box>
            </Typography>
            <Typography sx={{ fontSize: { xs: 16, md: 19 }, color: "#6b4a3a", maxWidth: 640, mx: "auto" }}>
              We're not just making pizza — we're crafting moments. Since 2010, every pie that leaves our oven carries a piece of our soul.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* MISSION */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Box
                sx={{
                  aspectRatio: "1/1",
                  borderRadius: 6,
                  background: REDGRAD,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: { xs: 180, md: 260 },
                  boxShadow: "0 30px 80px rgba(183,28,28,0.35)",
                }}
              >
                🔥
              </Box>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Typography sx={{ color: "#B71C1C", fontWeight: 700, letterSpacing: 2, fontSize: 13, mb: 1 }}>OUR MISSION</Typography>
              <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 44 }, mb: 3 }}>
                Real food. Real fire. Real flavor.
              </Typography>
              <Typography sx={{ fontSize: 17, color: "#6b4a3a", lineHeight: 1.7, mb: 2 }}>
                We believe great food brings people together. That's why we use only the freshest ingredients, hand-toss every dough, and fire every pizza in real wood-burning ovens — just like Nonna used to.
              </Typography>
              <Typography sx={{ fontSize: 17, color: "#6b4a3a", lineHeight: 1.7 }}>
                No shortcuts. No compromises. Just honest, soul-warming food made with passion.
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* VALUES */}
      <Box sx={{ background: "linear-gradient(180deg, #FFF8F0 0%, #FFF1E0 100%)", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Stack spacing={1} sx={{ textAlign: "center", mb: 6 }}>
            <Typography sx={{ color: "#B71C1C", fontWeight: 700, letterSpacing: 2, fontSize: 13 }}>WHAT DRIVES US</Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 48 } }}>Our core values</Typography>
          </Stack>
          <Grid container spacing={3}>
            {VALUES.map((v, i) => (
              <Grid item xs={12} sm={6} md={3} key={v.title}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -8 }}
                >
                  <Card sx={{ p: 4, height: "100%", borderRadius: 4, textAlign: "center", boxShadow: "0 10px 30px rgba(0,0,0,0.05)" }}>
                    <Box
                      sx={{
                        width: 64, height: 64, mx: "auto", mb: 2,
                        borderRadius: "50%", background: AMBERGRAD,
                        color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                        "& svg": { fontSize: 32 },
                      }}
                    >
                      {v.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 1 }}>{v.title}</Typography>
                    <Typography sx={{ fontSize: 14, color: "#6b4a3a" }}>{v.desc}</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TIMELINE */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack spacing={1} sx={{ textAlign: "center", mb: 6 }}>
          <Typography sx={{ color: "#B71C1C", fontWeight: 700, letterSpacing: 2, fontSize: 13 }}>OUR JOURNEY</Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 48 } }}>16 years of fire</Typography>
        </Stack>
        <Box sx={{ position: "relative", pl: { xs: 4, md: 0 } }}>
          <Box
            sx={{
              position: "absolute",
              left: { xs: 12, md: "50%" },
              top: 0, bottom: 0, width: 3,
              background: REDGRAD,
              transform: { md: "translateX(-50%)" },
            }}
          />
          {TIMELINE.map((t, i) => (
            <motion.div
              key={t.year}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { md: i % 2 === 0 ? "flex-start" : "flex-end" },
                  mb: 4, position: "relative",
                }}
              >
                <Card
                  sx={{
                    width: { xs: "100%", md: "45%" },
                    p: 3, borderRadius: 4,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                    border: "1px solid rgba(183,28,28,0.1)",
                  }}
                >
                  <Chip label={t.year} sx={{ mb: 1.5, background: REDGRAD, color: "#fff", fontWeight: 800 }} />
                  <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 0.5 }}>{t.title}</Typography>
                  <Typography sx={{ fontSize: 14, color: "#6b4a3a" }}>{t.desc}</Typography>
                </Card>
                <Box
                  sx={{
                    position: "absolute",
                    left: { xs: -28, md: "50%" },
                    top: 24,
                    width: 18, height: 18, borderRadius: "50%",
                    background: "#FFB300",
                    border: "4px solid #fff",
                    boxShadow: "0 0 0 3px #B71C1C",
                    transform: { md: "translateX(-50%)" },
                  }}
                />
              </Box>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* TEAM */}
      <Box sx={{ background: "linear-gradient(180deg, #FFF1E0 0%, #FFF8F0 100%)", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Stack spacing={1} sx={{ textAlign: "center", mb: 6 }}>
            <Typography sx={{ color: "#B71C1C", fontWeight: 700, letterSpacing: 2, fontSize: 13 }}>THE FAMILY</Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 48 } }}>Meet the team</Typography>
          </Stack>
          <Grid container spacing={3}>
            {TEAM.map((m, i) => (
              <Grid item xs={6} md={3} key={m.name}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -8 }}
                >
                  <Card sx={{ p: 3, borderRadius: 4, textAlign: "center", boxShadow: "0 10px 30px rgba(0,0,0,0.06)" }}>
                    <Box
                      sx={{
                        width: { xs: 90, md: 120 },
                        height: { xs: 90, md: 120 },
                        mx: "auto", mb: 2, borderRadius: "50%",
                        background: AMBERGRAD,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: { xs: 50, md: 70 },
                        boxShadow: "0 10px 30px rgba(255,179,0,0.4)",
                      }}
                    >
                      {m.emoji}
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: 15, md: 17 } }}>{m.name}</Typography>
                    <Typography sx={{ fontSize: 13, color: "#B71C1C", fontWeight: 600 }}>{m.role}</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;
